'use client';

import { useEffect } from 'react';

/**
 * Das Verhalten des nativen `<details>`-Akkordeons, das `FaqAccordion` rendert:
 * **eines offen** — wer eines öffnet, schliesst jedes andere.
 *
 * 🔴 **WARUM ES DIESE DATEI GIBT.** Am 21.09.2026 stand dasselbe Verhalten
 * zweimal im Baum, unter demselben Namen `FaqBehavior`: einmal unter
 * `anti-profiling/` (38 Zeilen), einmal unter `faq/` (46 Zeilen). Mit `diff`
 * gemessen war die zweite eine **Obermenge** der ersten — derselbe
 * Umschalt-Block Zeichen für Zeichen, plus sieben Zeilen für die Sprungmarke.
 * Zwei Dateien für einen Unterschied von sieben Zeilen, und beide hingen am
 * **selben** zentralen Akkordeon.
 *
 * ⚠️ **`tiefenlink` ist vorgabemässig AUS.** Die Anti-Profiling-Seite löst
 * keine Sprungmarke auf; sie es jetzt tun zu lassen wäre eine
 * Verhaltensänderung an einer bestehenden Fläche, und die verbietet
 * „Änderungen sind additiv". Die FAQ-Seite schaltet ihn ein.
 *
 * Reine Verhaltensergänzung — dieses Bauteil rendert kein Markup.
 */
export function FaqAkkordeonVerhalten({
  tiefenlink = false,
}: {
  /** Eine `#marke` in der Adresse öffnet das zugehörige `<details>` beim Laden. */
  tiefenlink?: boolean;
}) {
  useEffect(() => {
    const items = Array.from(
      document.querySelectorAll<HTMLDetailsElement>('details.faq-item'),
    );

    const handlers = items.map((detail) => {
      const onToggle = () => {
        if (detail.open) {
          items.forEach((other) => {
            if (other !== detail && other.open) {
              other.open = false;
            }
          });
        }
      };
      detail.addEventListener('toggle', onToggle);
      return { detail, onToggle };
    });

    if (tiefenlink && location.hash) {
      const target = document.querySelector(location.hash);
      if (target instanceof HTMLDetailsElement) {
        target.open = true;
      }
    }

    return () => {
      handlers.forEach(({ detail, onToggle }) => {
        detail.removeEventListener('toggle', onToggle);
      });
    };
  }, [tiefenlink]);

  return null;
}
